import { Star } from "lucide-react";
import type { TodayScreenProps } from "../types";
import { NowCard, PullNext } from "../focus/Today";
import { PaperEmptyState, PaperPageHeader } from "./chrome";
import { PaperCheckbox } from "./TaskRow";

/** Focus mode: the first planned task as a Now card, then Up next and Pull next, in Paper's type and spacing. */
function FocusLayout(p: TodayScreenProps) {
  const { progress, slots, now, pullNext } = p;
  const nothingPlanned = progress.total === 0;
  return (
    <>
      {now ? (
        <NowCard
          now={now}
          p={p}
          variant="paper"
          renderCheck={({ onDone, interactive, title }) => (
            <PaperCheckbox checked={false} onToggle={() => interactive && onDone()} label={`Complete ${title}`} size={22} />
          )}
        />
      ) : (
        <PaperEmptyState
          message={
            nothingPlanned
              ? "Nothing planned for today."
              : p.focusLabel && p.planned.length === 0 && progress.done < progress.total
                ? `Nothing planned for ${p.focusLabel} today.`
                : "Everything planned is done."
          }
          action={nothingPlanned || !pullNext ? slots.addTasks : <PullNext model={pullNext} focusLabel={p.focusLabel} today={p.date} />}
        />
      )}
      {now && (
        <section aria-label="Up next" className="mt-8">
          {p.planned.length > 1 && (
            <div className="mb-1 flex h-8 items-center gap-1.5 border-b border-border/70 px-2.5">
              <h2 className="font-sans text-[13.5px] font-semibold tracking-normal text-foreground/85">Up next</h2>
              <span className="text-[12px] text-subtle tabular">{p.planned.length - 1}</span>
            </div>
          )}
          {slots.upNextList}
          {pullNext && (
            <div className="mt-2">
              <PullNext model={pullNext} focusLabel={p.focusLabel} today={p.date} />
            </div>
          )}
        </section>
      )}
    </>
  );
}

export function PaperTodayScreen(p: TodayScreenProps) {
  const { progress, slots } = p;
  const focusMode = !!p.focusMode && p.now !== undefined;
  return (
    <div>
      <PaperPageHeader
        title="Today"
        icon={<Star className="fill-[var(--star)] text-[var(--star)]" />}
        actions={
          <>
            {progress.total > 0 && (
              <span className="mr-2 whitespace-nowrap text-[13px] text-muted-foreground tabular" aria-live="polite">
                {progress.done} of {progress.total} done
              </span>
            )}
            {slots.focusModeToggle}
            {slots.modeToggle}
            {slots.addTasks}
          </>
        }
        below={<div className="flex flex-wrap items-center gap-2">{slots.focusChip}</div>}
      />
      {slots.carryover && <div className="mb-5">{slots.carryover}</div>}
      {focusMode ? <FocusLayout {...p} /> : slots.plannedList}
      {(p.due.length > 0 || slots.dueOutside) && (
        <div className={focusMode ? "mt-8" : "mt-6"}>
          {p.due.length > 0 && slots.dueList}
          {slots.dueOutside && <div className="mt-1">{slots.dueOutside}</div>}
        </div>
      )}
      {slots.completedList && <div className="mt-6">{slots.completedList}</div>}
    </div>
  );
}
